import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import {
  Target, Heart, Users, Eye, Zap, Shield,
  TrendingUp, AlertTriangle, ChevronRight, ArrowRight,
  MessageCircle, Quote
} from "lucide-react";
import { toast } from "sonner";

const API = "/api";

// ── Section Header ────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, color, bg }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: bg }}>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <h3 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>{title}</h3>
  </div>
);

// ── Trait Pill ────────────────────────────────────────────
const TraitPill = ({ text, color, bg, border }) => (
  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold"
    style={{ background: bg, color, border: `1px solid ${border}` }}>
    {text}
  </span>
);

// ── Insight Quote Card ────────────────────────────────────
const InsightQuote = ({ name, role, picture, comment, suggestions }) => {
  const hasSuggestions = suggestions && Object.values(suggestions).some(arr => arr?.length > 0);
  if (!comment && !hasSuggestions) return null;

  return (
    <div className="rounded-xl p-4" style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}>
      <div className="flex items-center gap-2.5 mb-3">
        <Avatar className="h-8 w-8">
          {picture && <AvatarImage src={picture} alt={name} />}
          <AvatarFallback className="text-xs font-semibold"
            style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}>
            {name?.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{name}</p>
          <p className="text-[10px] capitalize" style={{ color: "var(--color-text-interactive)" }}>
            {(role || "").replace(/_/g, " ")}
          </p>
        </div>
      </div>

      {comment && (
        <div className="mb-3 pl-3" style={{ borderLeft: "2px solid var(--color-brand-light)" }}>
          <p className="text-sm italic" style={{ color: "var(--color-text-secondary)", lineHeight: 1.7 }}>
            "{comment}"
          </p>
        </div>
      )}

      {hasSuggestions && (
        <div className="flex flex-wrap gap-1.5">
          {(suggestions.strengths || []).map((s, i) => (
            <TraitPill key={`s-${i}`} text={s}
              color="var(--color-strengths-text)" bg="var(--color-strengths-bg)" border="var(--color-strengths-border)" />
          ))}
          {(suggestions.opportunities || []).map((s, i) => (
            <TraitPill key={`o-${i}`} text={s}
              color="var(--color-opportunities-text)" bg="var(--color-opportunities-bg)" border="var(--color-opportunities-border)" />
          ))}
          {(suggestions.weaknesses || []).map((s, i) => (
            <TraitPill key={`w-${i}`} text={s}
              color="var(--color-weaknesses-text)" bg="var(--color-weaknesses-bg)" border="var(--color-weaknesses-border)" />
          ))}
          {(suggestions.unsure || []).map((s, i) => (
            <TraitPill key={`u-${i}`} text={s}
              color="var(--color-text-secondary)" bg="var(--color-surface-subtle)" border="var(--color-border-default)" />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Empty Prompt ──────────────────────────────────────────
const EmptyPrompt = ({ icon: Icon, title, subtitle, action, actionLabel, bg, color }) => (
  <div className="rounded-xl p-6 text-center" style={{ background: bg, border: "1px dashed var(--color-border-active)" }}>
    <Icon className="w-8 h-8 mx-auto mb-2" style={{ color }} />
    <p className="text-sm font-semibold mb-1" style={{ color: "var(--color-text-primary)" }}>{title}</p>
    <p className="text-xs mb-3" style={{ color: "var(--color-text-secondary)" }}>{subtitle}</p>
    {action && (
      <button onClick={action} className="text-xs font-semibold px-4 py-1.5 rounded-full transition-all hover:opacity-80"
        style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}>
        {actionLabel}
        <ArrowRight className="w-3 h-3 inline ml-1" />
      </button>
    )}
  </div>
);

// ── Main Self-Portrait Page ───────────────────────────────
const SelfPortraitPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [swot, setSwot] = useState(null);
  const [values, setValues] = useState(null);
  const [swotInsights, setSwotInsights] = useState([]);
  const [valuesInsights, setValuesInsights] = useState([]);
  const [network, setNetwork] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [swotRes, valuesRes, swotInsRes, valInsRes, netRes, progRes] = await Promise.all([
        fetch(`${API}/tools/swot`, { credentials: "include" }),
        fetch(`${API}/tools/values`, { credentials: "include" }),
        fetch(`${API}/insights/tool/swot`, { credentials: "include" }),
        fetch(`${API}/insights/tool/core_values`, { credentials: "include" }),
        fetch(`${API}/network`, { credentials: "include" }),
        fetch(`${API}/dashboard/progress`, { credentials: "include" }),
      ]);
      if (swotRes.ok) setSwot(await swotRes.json());
      if (valuesRes.ok) setValues(await valuesRes.json());
      if (swotInsRes.ok) setSwotInsights(await swotInsRes.json());
      if (valInsRes.ok) setValuesInsights(await valInsRes.json());
      if (netRes.ok) setNetwork(await netRes.json());
      if (progRes.ok) setProgress(await progRes.json());
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--color-brand-primary)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  const strengths = (swot?.strengths || []).filter(s => !s.deleted);
  const weaknesses = (swot?.weaknesses || []).filter(s => !s.deleted);
  const opportunities = (swot?.opportunities || []).filter(s => !s.deleted);
  const threats = (swot?.threats || []).filter(s => !s.deleted);
  const allSwotItems = [...strengths, ...weaknesses, ...opportunities, ...threats];
  const topValues = (values?.values || []).sort((a, b) => a.priority - b.priority);
  const accepted = network.filter(c => c.status === "accepted");
  const completedInsights = [...swotInsights, ...valuesInsights].filter(i => i.status === "completed");
  const insightsWithContent = completedInsights.filter(i => i.comment || (i.suggested_swot_items && Object.values(i.suggested_swot_items).some(a => a?.length > 0)));

  const swotProgress = progress?.myself?.tools?.swot?.progress || 0;
  const valuesProgress = progress?.myself?.tools?.core_values?.progress || 0;
  const overallProgress = Math.round((swotProgress + valuesProgress) / 2);
  const hasAnyData = allSwotItems.length > 0 || topValues.length > 0;

  // Aggregate all suggested items from circle
  const circleSuggested = { strengths: [], weaknesses: [], opportunities: [], threats: [], unsure: [] };
  for (const ins of swotInsights) {
    const items = ins.suggested_swot_items || {};
    for (const cat of Object.keys(circleSuggested)) {
      for (const item of (items[cat] || [])) {
        if (!circleSuggested[cat].includes(item)) circleSuggested[cat].push(item);
      }
    }
  }
  const hasCircleSuggestions = Object.values(circleSuggested).some(a => a.length > 0);

  return (
    <div data-testid="self-portrait-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">

        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-2">
          <Link to="/myself" className="text-xs font-medium flex items-center gap-1"
            style={{ color: "var(--color-text-interactive)" }} data-testid="back-to-myself">
            Myself <ChevronRight className="w-3 h-3" /> Self-Portrait
          </Link>
        </div>

        {/* ── Hero Profile ── */}
        <div className="rounded-2xl overflow-hidden mb-8"
          style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}>
          <div className="h-24 relative"
            style={{ background: "linear-gradient(135deg, #1A1630 0%, #26215C 40%, #534AB7 100%)" }}>
            <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
              <div className="relative">
                <Avatar className="h-24 w-24 ring-4 ring-white shadow-xl" data-testid="portrait-avatar">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-3xl font-bold"
                    style={{ background: "linear-gradient(145deg, #534AB7, #7F77DD)", color: "#EEEDFE" }}>
                    {user?.name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                {/* Overall progress ring */}
                <div className="absolute -inset-1.5 pointer-events-none">
                  <svg width="108" height="108" className="-rotate-90">
                    <circle cx="54" cy="54" r="51" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
                    <circle cx="54" cy="54" r="51" fill="none" stroke="#AFA9EC" strokeWidth="3"
                      strokeDasharray={2 * Math.PI * 51}
                      strokeDashoffset={2 * Math.PI * 51 * (1 - overallProgress / 100)}
                      strokeLinecap="round" className="transition-all duration-1000" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-16 pb-6 px-6 text-center">
            <h1 className="text-2xl font-bold" style={{ color: "var(--color-text-primary)" }} data-testid="portrait-name">
              {user?.name}
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              {hasAnyData
                ? `Self-awareness: ${overallProgress}% explored`
                : "Your self-portrait starts here. Complete tools to fill it in."}
            </p>
            {accepted.length > 0 && (
              <p className="text-xs mt-2" style={{ color: "var(--color-text-interactive)" }}>
                {completedInsights.length} perspective{completedInsights.length !== 1 ? "s" : ""} from {accepted.length} people in your circle
              </p>
            )}
          </div>
        </div>

        {/* ── Content ── */}
        {!hasAnyData ? (
          /* Fully empty state */
          <div className="space-y-4">
            <EmptyPrompt icon={Target} title="Start your self-portrait"
              subtitle="Begin with your Personal SWOT — map your strengths, weaknesses, opportunities, and threats."
              action={() => navigate("/tools/swot")} actionLabel="Start SWOT"
              bg="var(--color-strengths-bg)" color="var(--color-strengths-text)" />
            <EmptyPrompt icon={Heart} title="Discover your values"
              subtitle="What matters most to you? Explore 62 curated values and pick your top 5."
              action={() => navigate("/tools/values")} actionLabel="Explore Values"
              bg="var(--color-brand-ghost)" color="var(--color-brand-primary)" />
          </div>
        ) : (
          <div className="space-y-8">

            {/* ── Your Landscape (SWOT) ── */}
            <section data-testid="portrait-swot-section">
              <SectionHeader icon={Target} title="Your Landscape" color="var(--color-strengths-text)" bg="var(--color-strengths-bg)" />

              {allSwotItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Strengths */}
                  <div className="rounded-xl p-4" style={{ background: "var(--color-strengths-bg)", border: "0.5px solid var(--color-strengths-border)" }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4" style={{ color: "var(--color-strengths-text)" }} />
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--color-strengths-text)" }}>
                        Strengths
                      </p>
                    </div>
                    {strengths.length > 0 ? (
                      <ul className="space-y-1.5">
                        {strengths.map((s, i) => (
                          <li key={s.id || i} className="text-sm" style={{ color: "var(--color-strengths-label)" }}>
                            {s.text || s.content}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs italic" style={{ color: "var(--color-text-placeholder)" }}>Not yet explored</p>
                    )}
                  </div>

                  {/* Weaknesses */}
                  <div className="rounded-xl p-4" style={{ background: "var(--color-weaknesses-bg)", border: "0.5px solid var(--color-weaknesses-border)" }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-4 h-4" style={{ color: "var(--color-weaknesses-text)" }} />
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--color-weaknesses-text)" }}>
                        Growth Areas
                      </p>
                    </div>
                    {weaknesses.length > 0 ? (
                      <ul className="space-y-1.5">
                        {weaknesses.map((s, i) => (
                          <li key={s.id || i} className="text-sm" style={{ color: "var(--color-weaknesses-label)" }}>
                            {s.text || s.content}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs italic" style={{ color: "var(--color-text-placeholder)" }}>Not yet explored</p>
                    )}
                  </div>

                  {/* Opportunities */}
                  <div className="rounded-xl p-4" style={{ background: "var(--color-opportunities-bg)", border: "0.5px solid var(--color-opportunities-border)" }}>
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4" style={{ color: "var(--color-opportunities-text)" }} />
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--color-opportunities-text)" }}>
                        Opportunities
                      </p>
                    </div>
                    {opportunities.length > 0 ? (
                      <ul className="space-y-1.5">
                        {opportunities.map((s, i) => (
                          <li key={s.id || i} className="text-sm" style={{ color: "var(--color-opportunities-label)" }}>
                            {s.text || s.content}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs italic" style={{ color: "var(--color-text-placeholder)" }}>Not yet explored</p>
                    )}
                  </div>

                  {/* Threats */}
                  <div className="rounded-xl p-4" style={{ background: "var(--color-threats-bg)", border: "0.5px solid var(--color-threats-border)" }}>
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4" style={{ color: "var(--color-threats-text)" }} />
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--color-threats-text)" }}>
                        Threats
                      </p>
                    </div>
                    {threats.length > 0 ? (
                      <ul className="space-y-1.5">
                        {threats.map((s, i) => (
                          <li key={s.id || i} className="text-sm" style={{ color: "var(--color-threats-label)" }}>
                            {s.text || s.content}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs italic" style={{ color: "var(--color-text-placeholder)" }}>Not yet explored</p>
                    )}
                  </div>
                </div>
              ) : (
                <EmptyPrompt icon={Target} title="Map your landscape"
                  subtitle="Your SWOT analysis will appear here as you complete it."
                  action={() => navigate("/tools/swot")} actionLabel="Start SWOT"
                  bg="var(--color-strengths-bg)" color="var(--color-strengths-text)" />
              )}

              {/* SWOT completion prompt */}
              {allSwotItems.length > 0 && swotProgress < 100 && (
                <button onClick={() => navigate("/tools/swot")}
                  className="mt-3 w-full text-center text-xs font-medium py-2 rounded-lg transition-colors hover:opacity-80"
                  style={{ color: "var(--color-text-interactive)", background: "var(--color-surface-subtle)" }}
                  data-testid="continue-swot-btn">
                  Continue building your SWOT ({swotProgress}% complete)
                  <ChevronRight className="w-3 h-3 inline ml-1" />
                </button>
              )}
            </section>

            {/* ── Your Values ── */}
            <section data-testid="portrait-values-section">
              <SectionHeader icon={Heart} title="Your Values" color="var(--color-brand-primary)" bg="var(--color-brand-ghost)" />

              {topValues.length > 0 ? (
                <div className="rounded-xl p-5" style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}>
                  <div className="flex flex-wrap gap-2">
                    {topValues.map((v, i) => (
                      <div key={v.value_id} className="flex items-center gap-2 px-3 py-2 rounded-lg"
                        style={{
                          background: i < 3 ? "var(--color-brand-ghost)" : "var(--color-surface-subtle)",
                          border: `1px solid ${i < 3 ? "var(--color-border-active)" : "var(--color-border-default)"}`,
                        }}>
                        <span className="text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                          style={{
                            background: i < 3 ? "var(--color-brand-primary)" : "var(--color-border-default)",
                            color: i < 3 ? "var(--color-text-on-brand)" : "var(--color-text-secondary)",
                          }}>
                          {i + 1}
                        </span>
                        <span className="text-sm font-semibold"
                          style={{ color: i < 3 ? "var(--color-brand-primary)" : "var(--color-text-primary)" }}>
                          {v.name}
                        </span>
                      </div>
                    ))}
                  </div>
                  {valuesProgress < 100 && (
                    <button onClick={() => navigate("/tools/values")}
                      className="mt-3 w-full text-center text-xs font-medium py-2 rounded-lg transition-colors hover:opacity-80"
                      style={{ color: "var(--color-text-interactive)", background: "var(--color-surface-subtle)" }}
                      data-testid="continue-values-btn">
                      Refine your values ({valuesProgress}% complete)
                      <ChevronRight className="w-3 h-3 inline ml-1" />
                    </button>
                  )}
                </div>
              ) : (
                <EmptyPrompt icon={Heart} title="What do you stand for?"
                  subtitle="Explore 62 curated values and identify the ones that truly guide your decisions."
                  action={() => navigate("/tools/values")} actionLabel="Discover Values"
                  bg="var(--color-brand-ghost)" color="var(--color-brand-primary)" />
              )}
            </section>

            {/* ── How Your Circle Sees You ── */}
            <section data-testid="portrait-circle-section">
              <SectionHeader icon={Eye} title="How Your Circle Sees You" color="var(--color-text-interactive)" bg="var(--color-surface-subtle)" />

              {/* Circle suggested traits aggregation */}
              {hasCircleSuggestions && (
                <div className="rounded-xl p-5 mb-4" style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3"
                    style={{ color: "var(--color-text-interactive)" }}>
                    Traits your circle identified
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {circleSuggested.strengths.map((s, i) => (
                      <TraitPill key={`cs-${i}`} text={s}
                        color="var(--color-strengths-text)" bg="var(--color-strengths-bg)" border="var(--color-strengths-border)" />
                    ))}
                    {circleSuggested.opportunities.map((s, i) => (
                      <TraitPill key={`co-${i}`} text={s}
                        color="var(--color-opportunities-text)" bg="var(--color-opportunities-bg)" border="var(--color-opportunities-border)" />
                    ))}
                    {circleSuggested.weaknesses.map((s, i) => (
                      <TraitPill key={`cw-${i}`} text={s}
                        color="var(--color-weaknesses-text)" bg="var(--color-weaknesses-bg)" border="var(--color-weaknesses-border)" />
                    ))}
                    {circleSuggested.unsure.map((s, i) => (
                      <TraitPill key={`cu-${i}`} text={s}
                        color="var(--color-text-secondary)" bg="var(--color-surface-subtle)" border="var(--color-border-default)" />
                    ))}
                  </div>
                </div>
              )}

              {/* Individual perspectives */}
              {insightsWithContent.length > 0 ? (
                <div className="space-y-3">
                  {insightsWithContent.map((ins) => (
                    <InsightQuote
                      key={ins.request_id}
                      name={ins.responder_name}
                      role={ins.responder_role}
                      picture={ins.responder_picture}
                      comment={ins.comment}
                      suggestions={ins.suggested_swot_items}
                    />
                  ))}
                </div>
              ) : completedInsights.length > 0 ? (
                <div className="rounded-xl p-5 text-center"
                  style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}>
                  <Users className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--color-brand-light)" }} />
                  <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {completedInsights.length} perspective{completedInsights.length > 1 ? "s" : ""} received.
                  </p>
                  <p className="text-xs mt-1" style={{ color: "var(--color-text-placeholder)" }}>
                    Insights from your circle will be aggregated here as they share detailed feedback.
                  </p>
                </div>
              ) : (
                <EmptyPrompt icon={Users} title="Get outside perspective"
                  subtitle={accepted.length > 0
                    ? `You have ${accepted.length} people in your circle. Ask them how they see you.`
                    : "Invite people you trust and ask for their honest perspective."
                  }
                  action={() => navigate(accepted.length > 0 ? "/myself" : "/network")}
                  actionLabel={accepted.length > 0 ? "Request Insight" : "Build Your Circle"}
                  bg="var(--color-surface-subtle)" color="var(--color-text-interactive)" />
              )}
            </section>

            {/* ── What's Still Unexplored ── */}
            {(swotProgress < 100 || valuesProgress < 100) && (
              <section data-testid="portrait-unexplored">
                <div className="rounded-xl p-5" style={{ background: "var(--color-surface-subtle)", border: "1px dashed var(--color-border-active)" }}>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2"
                    style={{ color: "var(--color-text-interactive)" }}>
                    Still to explore
                  </p>
                  <p className="text-sm" style={{ color: "var(--color-text-secondary)", lineHeight: 1.7 }}>
                    {overallProgress < 50
                      ? "Your self-portrait is just getting started. The more you explore, the clearer the picture becomes."
                      : "You're more than halfway there. Keep going — every tool adds a new dimension."
                    }
                  </p>
                  <div className="flex gap-2 mt-3">
                    {swotProgress < 100 && (
                      <button onClick={() => navigate("/tools/swot")}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full"
                        style={{ background: "var(--color-strengths-bg)", color: "var(--color-strengths-text)", border: "1px solid var(--color-strengths-border)" }}>
                        Continue SWOT
                      </button>
                    )}
                    {valuesProgress < 100 && (
                      <button onClick={() => navigate("/tools/values")}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full"
                        style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)", border: "1px solid var(--color-border-active)" }}>
                        Explore Values
                      </button>
                    )}
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfPortraitPage;
